/**
 * API Endpoints Configuration
 * Centralized endpoint definitions for all API calls
 * Uses the base URLs from api-config.ts
 */

import { API_CONFIG } from './api-config';

/**
 * Client Management Endpoints
 */
export const CLIENT_ENDPOINTS = {
  // List all clients with optional filters
  list: (params?: Record<string, any>) => {
    const queryString = params ? '?' + new URLSearchParams(params).toString() : '';
    return `${API_CONFIG.GATEWAY_URL}/api/clients${queryString}`;
  },
  
  // Get specific client by ID
  get: (clientId: string) => `${API_CONFIG.GATEWAY_URL}/api/clients/${clientId}`,
  
  // Create new client
  create: () => `${API_CONFIG.GATEWAY_URL}/api/clients`,
  
  // Update existing client
  update: (clientId: string) => `${API_CONFIG.GATEWAY_URL}/api/clients/${clientId}`,
  
  // Delete client
  delete: (clientId: string) => `${API_CONFIG.GATEWAY_URL}/api/clients/${clientId}`,
  
  // Get client documents
  documents: (clientId: string) => `${API_CONFIG.GATEWAY_URL}/api/clients/${clientId}/documents`,
  
  // Get client portfolio
  portfolio: (clientId: string) => `${API_CONFIG.GATEWAY_URL}/api/clients/${clientId}/portfolio`,
};

/**
 * Meeting Management Endpoints
 */
export const MEETING_ENDPOINTS = {
  // TASK-648: pruned to the single entry with a live caller (meetingChunkUpload).
  // The removed list/create paths hit the deleted /meetings alias (307 loop);
  // get/update/delete and /preparation/* pointed at routes no service serves.
  // Live meeting calls go through services/api/meetingApi.ts instead.

  // TASK-420: 30s WebM/Opus audio chunk upload. Backend (TASK-421) stitches by
  // `seq`, transcodes via ffmpeg, enqueues for Whisper, and pushes
  // `chunk_transcribed` WS messages back to the cockpit.
  audioChunk: (sessionId: string) => `${API_CONFIG.GATEWAY_URL}/api/meeting/${sessionId}/chunk`,
};

/**
 * Brief Management Endpoints
 */
export const BRIEF_ENDPOINTS = {
  // Get brief status
  status: (briefId: string) => `${API_CONFIG.GATEWAY_URL}/api/briefs/status/${briefId}`,
  
  // Get brief details
  get: (briefId: string) => `${API_CONFIG.GATEWAY_URL}/api/briefs/${briefId}`,
  
  // Create brief
  create: () => `${API_CONFIG.GATEWAY_URL}/api/briefs`,
};

/**
 * Market Data Endpoints
 */
export const MARKET_ENDPOINTS = {
  // Market data status
  status: () => `${API_CONFIG.GATEWAY_URL}/api/market/status`,
  
  // Market data configuration
  config: () => `${API_CONFIG.GATEWAY_URL}/api/market/config`,
  
  // Test market data connection
  testConnection: () => `${API_CONFIG.GATEWAY_URL}/api/market/test-connection`,
  
  // Disconnect market data
  disconnect: () => `${API_CONFIG.GATEWAY_URL}/api/market/disconnect`,
  
  // Get quote for symbol
  quote: (symbol: string) => `${API_CONFIG.GATEWAY_URL}/api/market/quote/${symbol}`,
};

/**
 * Authentication & OAuth Endpoints
 */
export const AUTH_ENDPOINTS = {
  // OAuth configuration
  config: () => `${API_CONFIG.GATEWAY_URL}/api/auth/config`,
  
  // OAuth status
  status: () => `${API_CONFIG.GATEWAY_URL}/api/auth/oauth/status`,
  
  // Microsoft OAuth
  microsoft: {
    authorize: () => `${API_CONFIG.GATEWAY_URL}/api/auth/oauth/microsoft/authorize`,
    callback: () => `${API_CONFIG.FRONTEND_URL}/auth/oauth/microsoft/callback`,
    token: () => `${API_CONFIG.GATEWAY_URL}/api/auth/oauth/microsoft/token`,
    dataPreview: () => `${API_CONFIG.GATEWAY_URL}/auth/oauth/microsoft/data-preview`,
  },
  
  // Disconnect OAuth — /auth/... (gateway proxies /auth/{path}; account-less token 401s on /api/auth/...). TASK-548(c)
  disconnect: () => `${API_CONFIG.GATEWAY_URL}/auth/oauth/disconnect`,
};

/**
 * File Upload Endpoints (TUS Protocol)
 */
export const UPLOAD_ENDPOINTS = {
  // Initialize upload
  init: () => `${API_CONFIG.GATEWAY_URL}/uploads`,
  
  // Upload chunk
  chunk: (uploadId: string) => `${API_CONFIG.GATEWAY_URL}/uploads/${uploadId}`,
  
  // Get upload status
  status: (uploadId: string) => `${API_CONFIG.GATEWAY_URL}/uploads/${uploadId}`,
  
  // Delete upload
  delete: (uploadId: string) => `${API_CONFIG.GATEWAY_URL}/uploads/${uploadId}`,
  
  // Preview uploaded data
  preview: (uploadId: string) => `${API_CONFIG.GATEWAY_URL}/uploads/${uploadId}/preview`,
  
  // Confirm upload
  confirm: (uploadId: string) => `${API_CONFIG.GATEWAY_URL}/uploads/${uploadId}/confirm`,
};

/**
 * WebSocket Endpoints
 * ✅ TASK-083: All WebSocket connections go through gateway for account isolation and JWT authentication
 */
export const WEBSOCKET_ENDPOINTS = {
  // Live meeting WebSocket (via gateway for account isolation)
  liveMeeting: (sessionId: string) => `${API_CONFIG.WS_GATEWAY_URL}/ws/live-meeting/${sessionId}`,

  // Meeting preparation WebSocket (via gateway)
  preparation: (sessionId: string) => `${API_CONFIG.WS_GATEWAY_URL}/ws/preparation/${sessionId}`,

  // Upload progress WebSocket (via gateway)
  uploadProgress: (uploadId: string) => `${API_CONFIG.WS_GATEWAY_URL}/ws/uploads/${uploadId}`,

  // General meeting WebSocket (via gateway)
  meeting: (sessionId: string) => `${API_CONFIG.WS_GATEWAY_URL}/ws/${sessionId}`,
};

/**
 * Portfolio Analysis Endpoints
 */
export const PORTFOLIO_ENDPOINTS = {
  // Analyze portfolio (currently direct to agent, should go through gateway)
  analyze: () => `${API_CONFIG.GATEWAY_URL}/api/portfolio/analyze`,
  
  // Get portfolio summary
  summary: (clientId: string) => `${API_CONFIG.GATEWAY_URL}/api/portfolio/${clientId}/summary`,
  
  // Get portfolio performance
  performance: (clientId: string) => `${API_CONFIG.GATEWAY_URL}/api/portfolio/${clientId}/performance`,
};

/**
 * Agent Service Endpoints
 * These are typically internal but exposed for direct agent communication if needed
 */
export const AGENT_ENDPOINTS = {
  // Client profile agent
  clientProfile: {
    generate: () => `${API_CONFIG.GATEWAY_URL}/api/agents/client-profile/generate`,
  },
  
  // Note taking agent
  note: {
    create: () => `${API_CONFIG.GATEWAY_URL}/api/agents/note/create`,
  },
  
  // Email agent
  email: {
    send: () => `${API_CONFIG.GATEWAY_URL}/api/agents/email/send`,
    draft: () => `${API_CONFIG.GATEWAY_URL}/api/agents/email/draft`,
  },
};

/**
 * Health Check Endpoints
 */
export const HEALTH_ENDPOINTS = {
  // Gateway health
  gateway: () => `${API_CONFIG.GATEWAY_URL}/health`,
  
  // Orchestrator health
  orchestrator: () => `${API_CONFIG.ORCHESTRATOR_URL}/health`,
  
  // Data service health
  data: () => `${API_CONFIG.DATA_URL}/health`,
  // TASK-584: market health probe removed (unused; market-data merged into portfolio-service).
};

/**
 * Helper function to build URL with query parameters
 */
export function buildUrl(baseUrl: string, params?: Record<string, any>): string {
  if (!params || Object.keys(params).length === 0) {
    return baseUrl;
  }
  
  const queryString = new URLSearchParams(params).toString();
  return `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}${queryString}`;
}

/**
 * Export a single object for easy access
 */
export const API_ENDPOINTS = {
  clients: CLIENT_ENDPOINTS,
  meetings: MEETING_ENDPOINTS,
  briefs: BRIEF_ENDPOINTS,
  market: MARKET_ENDPOINTS,
  auth: AUTH_ENDPOINTS,
  uploads: UPLOAD_ENDPOINTS,
  websocket: WEBSOCKET_ENDPOINTS,
  portfolio: PORTFOLIO_ENDPOINTS,
  agents: AGENT_ENDPOINTS,
  health: HEALTH_ENDPOINTS,
};